import { navigateTo } from './navigationHandler';
import { toast } from '../utils/toast';
import i18n from '../i18n';

let isHandling401: boolean = false;
let hasShownAuthError: boolean = false;

interface ApiClientOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  skipAuth?: boolean; // For public endpoints like login
}

export interface ApiErrorData {
  error?: {
    errorKey?: string;
    additionalInfo?: string | {
      message?: string;
      stack?: string;
    };
  };
}

export class ApiError extends Error {
  errorData?: ApiErrorData;
  status?: number;
  
  constructor(message: string, errorData?: ApiErrorData, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.errorData = errorData;
    this.status = status;
  }
}

export class SubscriptionRequiredError extends Error {
  subscriptionType?: 'autoSearch' | 'autoNego' | 'mcp' | 'general';
  
  constructor(message: string, subscriptionType?: 'autoSearch' | 'autoNego' | 'mcp' | 'general') {
    super(message);
    this.name = 'SubscriptionRequiredError';
    this.subscriptionType = subscriptionType;
  }
}


export class AuthError extends Error {
  static ERROR_KEY = 'auth.sessionExpired'; // Translation key

  constructor() {
    super(AuthError.ERROR_KEY);
    this.name = 'AuthError';
  }
}

export const isAuthError = (error: unknown): boolean => error instanceof AuthError;

export const isSubscriptionError = (error: unknown): boolean => error instanceof SubscriptionRequiredError;


export const handleApiError = (error: unknown, errorMessage: string): void => {
  if (!isAuthError(error) && !isSubscriptionError(error)) {
    toast.error(errorMessage);
  }
};

/**
 * Safe error toast that won't show auth-related errors (those are handled globally)
 * Use this in catch blocks to display user-friendly error messages
 */
export const showErrorToast = (error: unknown, fallbackMessage: string): void => {
  if (isAuthError(error) || isSubscriptionError(error)) {
    return;
  }

  // Handle geocoding errors with failed addresses
  if (error instanceof ApiError && error.errorData?.error?.errorKey === 'client.body.geocoding') {
    const additionalInfo = error.errorData.error.additionalInfo as any;
    if (additionalInfo?.failedAddresses && Array.isArray(additionalInfo.failedAddresses)) {
      const failedAddressesStr = additionalInfo.failedAddresses
        .map((addr: any) => `${addr.city}, ${addr.postalCode} ${addr.country}`)
        .join('; ');
      const message = `Failed to geocode addresses: ${failedAddressesStr}`;
      toast.error(message);
      return;
    }
  }

  toast.error(fallbackMessage);
};


export const apiClient = async (
  url: string, 
  options: ApiClientOptions = {}
): Promise<Response> => {
  const { skipAuth = false, headers = {}, ...fetchOptions } = options;

  const requestHeaders: Record<string, string> = {
    ...headers,
  };
  // Only add Content-Type if we have a body and no Content-Type was provided
  if (fetchOptions.body && !headers['Content-Type'] && !headers['content-type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  if (!skipAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }
  }
  const response = await fetch(url, {
    ...fetchOptions,
    headers: requestHeaders,
  });
  if (response.status === 401 && !skipAuth) { // Automatically handle session expiration
    // Try to parse error response body for subscription errors
    try {
      const errorData = await response.clone().json();
      
      // Check if it's a subscription error
      if (errorData?.error?.errorKey === 'client.body.auth' && 
          errorData?.error?.additionalInfo && 
          typeof errorData.error.additionalInfo === 'string' &&
          errorData.error.additionalInfo.includes('Subscription')) {
        
        // Determine subscription type from additionalInfo
        let subscriptionType: 'autoSearch' | 'autoNego' | 'mcp' | 'general' = 'general';
        if (errorData.error.additionalInfo.includes('autoSearch') || 
            errorData.error.additionalInfo.includes('AutoSearch')) {
          subscriptionType = 'autoSearch';
        } else if (errorData.error.additionalInfo.includes('autoNego') || 
                   errorData.error.additionalInfo.includes('AutoNego')) {
          subscriptionType = 'autoNego';
        } else if (errorData.error.additionalInfo.includes('mcp') || 
                   errorData.error.additionalInfo.includes('MCP')) {
          subscriptionType = 'mcp';
        }
        
        throw new SubscriptionRequiredError(
          'Subscription required for this feature',
          subscriptionType
        );
      }
    } catch (err) {
      // If it's a SubscriptionRequiredError, rethrow it
      if (err instanceof SubscriptionRequiredError) {
        throw err;
      }
      // Otherwise continue with normal 401 handling
    }
    
    // Only handle 401 once - prevent multiple simultaneous 401s from causing redirect loops
    if (!isHandling401) {
      isHandling401 = true;
      // Show user-friendly error message (only once)
      if (!hasShownAuthError) {
        const message = (i18n.t as any)(AuthError.ERROR_KEY);
        toast.error(message);
        hasShownAuthError = true;
      }
      // Clear all authentication data
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('token');
      // Dispatch custom event to notify AuthContext of logout
      window.dispatchEvent(new CustomEvent('auth:logout'));
      // Use React Router navigation if available, otherwise fall back to window.location
      navigateTo('/login', { replace: true });
      // Reset the flags after a short delay to allow future 401 handling (e.g., after a new login attempt)
      setTimeout(() => {
        isHandling401 = false;
        hasShownAuthError = false;
      }, 1000);
    }
    throw new AuthError();
  }
  // Validate all other non-ok responses
  if (!response.ok && response.status !== 401) {
    // Try to parse error response body for more detailed error information
    const errorData = await response.clone().json();
    const message = errorData.error?.message || JSON.stringify(errorData);
    throw new ApiError(message, errorData, response.status);
  }
  return response;
};

/**
 * Convenience method for GET requests
 */
export const apiGet = (url: string, query: any = {}, options?: Omit<ApiClientOptions, 'method'>) => {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      params[key] = String(value);
    }
  }
  const urlParams = new URLSearchParams(params);
  const separator = urlParams.toString() ? '?' : '';
  return apiClient(url + separator + urlParams, {...options, method: 'GET' });
};

/**
 * Convenience method for POST requests
 */
export const apiPost = <T = any>(url: string, data?: T, options?: Omit<ApiClientOptions, 'method' | 'body'>) => apiClient(url, { 
  ...options, 
  method: 'POST', 
  body: data ? JSON.stringify(data) : undefined 
});

/**
 * Convenience method for PUT requests
 */
export const apiPut = <T = any>(url: string, data?: T, options?: Omit<ApiClientOptions, 'method' | 'body'>) => apiClient(url, { 
  ...options, 
  method: 'PUT', 
  body: data ? JSON.stringify(data) : undefined 
});

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = (url: string, options?: Omit<ApiClientOptions, 'method'>) => apiClient(url, { ...options, method: 'DELETE' });
